import React, { useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import { action, computed, observable, makeAutoObservable } from 'mobx';
import { EventsCollectionQueryCompetitionWithEventsType } from 'src_common/common/websocket2/models/EventsCollectionQueryModel';
import classNames from 'classnames';
import { useAsObservableSource } from 'mobx-react-lite';
import { useAppStateContext } from 'src/AppState/AppState';
import { SelectionModel } from 'src_common/common/websocket2/models/SelectionModel/SelectionModel';
import { AntePostRaceSummarySelection } from './AntePostRaceSummarySelection';

const sortByPriceMethod = (selectionA: SelectionModel, selectionB: SelectionModel): number => {
    const priceA = selectionA.price?.d;
    const priceB = selectionB.price?.d;
    if (priceA !== undefined && priceB !== undefined) {
        const price = priceB - priceA;
        if (price === 0) {
            return selectionA.name > selectionB.name ? -1 : 1;
        }
        return price;
    }
    return 0;
};

const sortByName = (selectionA: SelectionModel, selectionB: SelectionModel): number => {
    return selectionA.name < selectionB.name ? -1 : 1;
};

const sortByUnnamedFavourite = (selection: SelectionModel): number => {
    return selection.templateId === 'unnamed-favourite' ? -1 : 0;
};

const sortByNonRunner = (selection: SelectionModel): number => {
    return selection.resultType === 'void' ? -1 : 0;
};

const sortByStartNumber = (selectionA: SelectionModel, selectionB: SelectionModel): number => {
    const metaDataNumberA = selectionA.metaDataNumber ?? null;
    const metaDataNumberB = selectionB.metaDataNumber ?? null;

    if (metaDataNumberA !== null && metaDataNumberB !== null) {
        return metaDataNumberA < metaDataNumberB ? 1 : -1;
    }

    return 0;
};

const getSortedSelections = (sport: string, selections: Array<SelectionModel>): Array<SelectionModel> => {
    if (sport === 'horseracing') {
        return selections
            .sort(sortByName)
            .sort(sortByPriceMethod)
            .sort(sortByNonRunner)
            .sort(sortByUnnamedFavourite)
            .reverse();
    }
    if (sport === 'greyhoundracing') {
        return selections.sort(sortByStartNumber).sort(sortByUnnamedFavourite).sort(sortByNonRunner).reverse();
    }

    return [];
};

interface PropsTypes {
    competition: EventsCollectionQueryCompetitionWithEventsType;
    activeCompetitionId: number | null;
    onOpenCompetition: (competitionId: number) => void;
    userId: number;
}

class State {
    @observable selectedEventId: number | null = null;

    public constructor(private readonly props: PropsTypes) {
        makeAutoObservable(this);
    }

    @computed get isOpen(): boolean {
        return this.props.activeCompetitionId === this.props.competition.id;
    }

    @action handleOpenCompetition = (): void => {
        this.props.onOpenCompetition(this.props.competition.id);
    };

    @action onSelectEvent = (selectedEventId: number): void => {
        if (this.selectedEventId === selectedEventId) {
            this.selectedEventId = null;
        } else {
            this.selectedEventId = selectedEventId;
        }
    };

    @action onCloseEvent = (): void => {
        this.selectedEventId = null;
    };
}

export const AntePostCompetition = observer('AntePostCompetition', (propsIn: PropsTypes) => {
    const { websocketV2 } = useAppStateContext();
    const props = useAsObservableSource(propsIn);
    const [state] = useState(() => new State(props));
    const { isOpen, handleOpenCompetition, onSelectEvent, selectedEventId, onCloseEvent } = state;
    const { competition } = props;
    const className = classNames('ante-post__competition', {
        'ante-post__competition--open': isOpen,
    });

    const selectedEvent = selectedEventId !== null ? websocketV2.getEvent(selectedEventId) : null;

    return (
        <li>
            <section className={className}>
                <header>
                    <button
                        className='ante-post__competition-name'
                        onClick={handleOpenCompetition}
                    >
                        {competition.name}
                    </button>
                </header>
                {isOpen ? (
                    selectedEvent !== null ? (
                        <section className='ante-post__event'>
                            <button
                                onClick={onCloseEvent}
                                className='ante-post__close-event'
                            >
                                {selectedEvent.name}
                            </button>
                            {selectedEvent.markets.map((market) => {
                                const selections = getSortedSelections(selectedEvent.sport, market.selections);
                                return (
                                    <div key={market.id}>
                                        <div className='ante-post__market-name'>
                                            {market.name} - {market.id}
                                        </div>
                                        <ul className='ante-post__race-summary'>
                                            {selections.map((selection) => {
                                                return (
                                                    <AntePostRaceSummarySelection
                                                        key={selection.id}
                                                        userId={props.userId}
                                                        sport={selectedEvent.sport}
                                                        selection={selection}
                                                    />
                                                );
                                            })}
                                        </ul>
                                    </div>
                                );
                            })}
                        </section>
                    ) : (
                        <ul className='ante-post__events-list'>
                            {competition.events.map((event) => {
                                return (
                                    <li key={event.id}>
                                        <button
                                            onClick={(): void => onSelectEvent(event.id)}
                                            className='ante-post__event-select'
                                        >
                                            {event.name}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )
                ) : null}
            </section>
        </li>
    );
});
